import { computed, Injectable, signal } from '@angular/core';
import { NetworkStatus } from './connectivity.model';

/**
 * @description
 * Platform agnostic source of truth for the device connectivity status.
 *
 * The state lives here (single writable signal in the base class) so every
 * consumer reacts to the same change, no matter which platform implementation
 * feeds it: the Capacitor Network plugin on native, or the browser capabilities
 * on web.
 *
 * Only `setStatus` writes the state; `isOnline` / `isOffline` are derived values
 * without a state of their own, so they can never disagree with each other.
 *
 * Known limitation: this only reflects what the platform reports. A Wi-Fi
 * network without internet access (captive portal, backend down) is reported as
 * connected; in that case requests fail through the regular error paths.
 */
@Injectable()
export abstract class ConnectivityService {
  /**
   * Assumed online until the platform reports a status, so the app does not warn
   * about being offline nor blocks actions while the status is still unknown.
   */
  private readonly _status = signal<NetworkStatus>({ connected: true });
  private readonly _reconnected = signal(0);

  public readonly status = this._status.asReadonly();
  public readonly isOnline = computed(() => this._status().connected);
  public readonly isOffline = computed(() => !this.isOnline());

  /** Increments on every offline -> online transition. */
  public readonly reconnected = this._reconnected.asReadonly();

  /** Whether the platform watchers were already set up (see `start`). */
  protected started = false;

  /**
   * Starts watching the platform connectivity status. It must be idempotent and must never reject:
   * the app bootstrap waits for it, so a failing or hung plugin cannot be allowed to break it.
   */
  public abstract start(): Promise<void>;

  protected setStatus(status: NetworkStatus): void {
    const wasOffline = !this._status().connected;

    this._status.set(status);

    if (wasOffline && status.connected) {
      this._reconnected.update((count) => count + 1);
    }
  }
}
